import type { JSONContent } from '@tiptap/core';
import type { MaybeRefOrGetter, Ref } from 'vue';
import type { UserInvitationAttributes } from '#layers/access/utils/extensions/user-invitation';
import type { MonospaceUser } from '#layers/data-model';
import { toValue } from 'vue';
import { isValidEmail } from '#layers/access/utils/email';
import { isUserInvitation } from '#layers/access/utils/extensions/user-invitation';
import { isNil } from '#layers/base';

export type InvitableUser = Pick<MonospaceUser, 'id' | 'email' | 'fullName'>;

export interface UseInvitationsOptions {
	invitations: Ref<UserInvitationAttributes[]>;
	availableUsers?: Ref<InvitableUser[] | null | undefined>;
	excludeUserIds?: MaybeRefOrGetter<string[]>;
}

export function useInvitations(options: UseInvitationsOptions) {
	const { invitations } = options;

	function getSuggestions(query: string): InvitableUser[] {
		const users = toValue(options.availableUsers) ?? [];
		const excluded = new Set(toValue(options.excludeUserIds));
		const existingEmails = new Set(invitations.value.map((r) => r.email.toLowerCase()));
		const q = query.toLowerCase();

		if (!q) return [];

		return users
			.filter((user) => !excluded.has(user.id))
			.filter((user) => !isNil(user.email) && !existingEmails.has(user.email.toLowerCase()))
			.filter((user) => !isNil(user.email) && (user.email.toLowerCase().includes(q) || user.fullName?.toLowerCase().includes(q)))
			.slice(0, 5);
	}

	function toEditorContent(): JSONContent {
		if (invitations.value.length === 0) {
			return { type: 'doc', content: [] };
		}

		const content: object[] = [];

		for (const invitation of invitations.value) {
			content.push({ type: 'invitation', attrs: invitation }, { type: 'text', text: ' ' });
		}

		return { type: 'doc', content };
	}

	function fromEditorJson(json: JSONContent) {
		if (!json.content) {
			invitations.value = [];
			return;
		}

		invitations.value = json.content
			.filter((node) => isUserInvitation(node))
			.map((node) => ({
				email: node.attrs.email as string,
				recipientType: node.attrs.recipientType as 'existing_member' | 'new_email',
				userId: (node.attrs.userId as string | null) ?? null,
				fullName: (node.attrs.fullName as string | null) ?? null,
			}));
	}

	function getEffectiveRecipients(pendingText: string): UserInvitationAttributes[] {
		const trimmed = pendingText.trim().toLowerCase();

		if (!trimmed || !isValidEmail(trimmed)) {
			return invitations.value;
		}

		const isDuplicate = invitations.value.some((r) => r.email.toLowerCase() === trimmed);

		if (isDuplicate) {
			return invitations.value;
		}

		return [
			...invitations.value,
			{ email: trimmed, recipientType: 'new_email', userId: null, fullName: null },
		];
	}

	return {
		getSuggestions,
		toEditorContent,
		fromEditorJson,
		getEffectiveRecipients,
	};
}
